/**
 * A base class for models with an identifier and utility methods.
 * Provides functionality for comparing and stringifying models.
 *
 * @author Jackson Bingham <jackson@onlineimage.com>
 * @date November 2024
 */
abstract class Model {
    /**
     * The unique identifier for the model instance.
     * @private
     */
    private readonly _id: string;

    /**
     * Creates an instance of the Model class.
     * @param id The unique identifier for the model.
     */
    protected constructor(id: string) {
        this._id = id;
    }

    /**
     * Gets the unique identifier of the model.
     * @returns The unique identifier of the model.
     *
     * @example
     * const model = new Model("123");
     * console.log(model.id); // "123"
     */
    public get id(): string {
        return this._id;
    }

    /**
     * Serializes all enumerable own properties to a plain object.
     * Automatically handles private fields (like `_field`) by stripping underscores.
     *
     * Subclasses can override this for custom output, but this covers most use cases.
     */
    public toJSON(): Record<string, any> {
        return Object.entries(this)
            .filter(([key]) => !key.startsWith('__')) // Skip any double-underscore internals
            .reduce((obj, [key, val]) => {
                // Strip leading underscore: _email => email
                const normalizedKey = key.startsWith('_') ? key.slice(1) : key;
                obj[normalizedKey] = val;
                return obj;
            }, {} as Record<string, any>);
    }

    /**
     * Converts the model instance to a JSON string representation.
     * All fields are stringified using JSON.stringify.
     *
     * @returns A stringified JSON representation of the model.
     *
     * @example
     * const model = new Model("123");
     * console.log(model.toString()); // '{"id":"123"}'
     */
    public toString(): string {
        return JSON.stringify(this);
    }

    /**
     * Compares the current model with another model for equality.
     * Models are considered equal if their `id` properties are the same.
     *
     * @param other The model to compare against.
     * @returns `true` if the models are equal, otherwise `false`.
     *
     * @example
     * const model1 = new Model("123");
     * const model2 = new Model("123");
     * const model3 = new Model("456");
     * console.log(model1.equals(model2)); // true
     * console.log(model1.equals(model3)); // false
     */
    public equals(other: Model): boolean {
        return this._id === other.id;
    }

    /**
     * Creates a deep clone of the model, preserving prototype methods.
     * Requires subclasses to implement a static `fromJson()` method.
     *
     * @example
     * const clone = User.clone(userInstance);
     */
    public static clone<T extends Model>(
        this: { fromJson(json: any): T },
        original: T
    ): T {
        return this.fromJson(JSON.parse(JSON.stringify(original)));
    }
}

export default Model;
